import { List } from '@react-admin/ra-rbac';
import {
  AutocompleteArrayInput,
  DatagridConfigurable,
  FunctionField,
  NumberField,
  NumberInput,
  RaRecord,
  ReferenceArrayInput,
  ReferenceField,
  TextField,
  TextInput,
  useCanAccess,
  useGetList,
  useListContext,
} from 'react-admin';
import { createFilterQuery, DEFAULT_LIST_PAGE_PAGINATION_LIMIT } from '../../utils';
import { ReactElement } from 'react';
import TzDateInput from '../../Components/TzDate/TzDateInput';
import StatisticsDisplay, { StatItem } from '../../Components/StatisticsDisplay';
import TzDateField from '../../Components/TzDate/TzDateField';

const filters = (canListUsers: boolean, canListProducts: boolean): ReactElement[] =>
  [
    <NumberInput alwaysOn key="filter.id" label="ID" source="equals.id" />,
    canListProducts && (
      <ReferenceArrayInput
        label="Product"
        key="filter.product.id"
        source="equals.product.id"
        reference="products"
        alwaysOn
      >
        <AutocompleteArrayInput
          label="Product"
          optionText="attributes.name"
          filterToQuery={createFilterQuery()}
        />
      </ReferenceArrayInput>
    ),
    canListUsers && (
      <ReferenceArrayInput
        label="User ID"
        key="filter.user.id"
        source="equals.user.id"
        reference="users"
        alwaysOn
      >
        <AutocompleteArrayInput
          label="User ID"
          optionText="id"
          filterToQuery={createFilterQuery()}
        />
      </ReferenceArrayInput>
    ),
    canListUsers && (
      <ReferenceArrayInput label="Kiosq ID" key="users" source="user.id" reference="users" alwaysOn>
        <AutocompleteArrayInput
          label="Kiosq ID"
          optionText="attributes.kiosq_id"
          filterToQuery={createFilterQuery(['kiosq_id'])}
        />
      </ReferenceArrayInput>
    ),
    <TextInput key="filter.system_id" label="System ID" source="contains.system_id" />,
    <NumberInput
      key="filter.price_threshold_gt"
      label="Price Threshold: More than"
      source="greaterThan.price_threshold"
    />,
    <NumberInput
      key="filter.price_threshold_lt"
      label="Price Threshold: Less than"
      source="lessThan.price_threshold"
    />,
    <TzDateInput
      key="filter.created_at_utc_lte"
      label="Created Before"
      source="lessOrEqual.created_at_utc"
      serverTimezone="UTC"
    />,
    <TzDateInput
      key="filter.created_at_utc_gte"
      label="Created After"
      source="greaterOrEqual.created_at_utc"
      serverTimezone="UTC"
    />,
    <TzDateInput
      key="filter.last_notification_utc_lte"
      label="Last Notification Before"
      source="lessOrEqual.last_notification_utc"
      serverTimezone="UTC"
    />,
    <TzDateInput
      key="filter.last_notification_utc_gte"
      label="Last Notification After"
      source="greaterOrEqual.last_notification_utc"
      serverTimezone="UTC"
    />,
  ].filter((value) => typeof value !== 'boolean');

const SubscriptionStats = ({ canListUsers }: { canListUsers: boolean }): ReactElement => {
  const {
    total: uniquePriceAlertCount,
    isLoading: isPriceAlertCountLoading,
    filterValues,
  } = useListContext();

  const { total: uniqueUsersCount, isLoading: isUserCountLoading } = useGetList('users', {
    pagination: { page: 1, perPage: 1 },
    filter: filterValues ? { has: { subscriptions: filterValues } } : undefined,
  });

  const stats: StatItem[] = [
    {
      key: 'Total Price Alerts',
      value: uniquePriceAlertCount ?? 0,
      color: 'primary',
      isLoading: isPriceAlertCountLoading,
    },
  ];

  if (canListUsers) {
    stats.push({
      key: 'Unique Users',
      value: uniqueUsersCount ?? 0,
      color: 'secondary' as const,
      isLoading: isUserCountLoading,
    });
  }

  return <StatisticsDisplay stats={stats} />;
};

export default function SubscriptionList(): ReactElement {
  const userAccess = useCanAccess({
    resource: 'users',
    action: 'list',
  });
  const productAccess = useCanAccess({
    resource: 'products',
    action: 'list',
  });

  const canListUsers = userAccess.canAccess ?? false;
  const canListProducts = productAccess.canAccess ?? false;

  const prefetch: string[] = [];
  if (canListUsers) prefetch.push('user');
  if (canListProducts) prefetch.push('product');

  return (
    <List
      filters={filters(canListUsers, canListProducts)}
      perPage={DEFAULT_LIST_PAGE_PAGINATION_LIMIT}
      sort={{ order: 'DESC', field: 'id' }}
      queryOptions={{ meta: { prefetch } }}
    >
      <>
        <SubscriptionStats canListUsers={canListUsers} />
        <DatagridConfigurable>
          <NumberField source="id" />
          <TextField source="attributes.system_id" label="System ID" />
          {canListProducts && (
            <FunctionField
              label="Product"
              render={(record: RaRecord) => {
                const systemId = record?.attributes?.system_id?.toLowerCase() || '';
                const productId = record?.relationships?.product?.data?.id;

                // Check if system_id contains "getprice" or "pricepanda"
                const isGetPriceOrPricePanda =
                  systemId.includes('getprice') || systemId.includes('pricepanda');

                if (!productId) {
                  return <span>Not assigned</span>;
                }

                if (isGetPriceOrPricePanda) {
                  // Show as ReferenceField for getprice/pricepanda systems
                  return (
                    <ReferenceField
                      record={record}
                      source="relationships.product.data.id"
                      reference="products"
                    >
                      <TextField source="attributes.name" />
                    </ReferenceField>
                  );
                } else {
                  return <TextField record={record} source="relationships.product.data.id" />;
                }
              }}
            />
          )}
          {canListUsers && <TextField label="User ID" source="relationships.user.data.id" />}
          {canListUsers && (
            <ReferenceField source="relationships.user.data.id" reference="users" label="Kiosq ID">
              <FunctionField
                label="Kiosq ID"
                render={(record: RaRecord) => {
                  const kiosqId = record?.attributes?.kiosq_id;

                  if (!kiosqId) {
                    return <i>Unknown</i>;
                  }

                  return (
                    <a
                      href={`https://manage.auth0.com/dashboard/eu/futureplc/users/${btoa(kiosqId)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {kiosqId}
                    </a>
                  );
                }}
              />
            </ReferenceField>
          )}
          <NumberField
            source="attributes.price_threshold"
            label="Price Threshold"
            options={{ style: 'currency', currency: 'AUD' }}
          />
          <TzDateField
            source="attributes.created_at_utc"
            label="Created At"
            serverTimezone="UTC"
            showTime
          />
          <TzDateField
            source="attributes.last_notification_utc"
            label="Last Notification"
            serverTimezone="UTC"
            showTime
          />
          <NumberField source="attributes.notifications_sent" label="Price Alerts Sent" />
        </DatagridConfigurable>
      </>
    </List>
  );
}
